import { useState, useEffect, useCallback } from "react";
import { 
  FileSignature, Download, Upload, MessageCircle, Mail, Phone, Building2,
  Calendar, Search, Eye, CheckCircle2, AlertTriangle, Clock, XCircle,
  FileText, Send, ChevronRight, Pencil, Printer
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

// Estados de contrato
const STATUS_CONFIG = {
  borrador: { label: "Borrador", color: "bg-gray-100 text-gray-700", icon: Pencil },
  enviado: { label: "Enviado", color: "bg-blue-100 text-blue-700", icon: Send },
  firmado: { label: "Firmado", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
  cancelado: { label: "Cancelado", color: "bg-red-100 text-red-700", icon: XCircle },
};

export default function ContratoTab() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Modales
  const [detalleOpen, setDetalleOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Formulario
  const [fechaFirma, setFechaFirma] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [archivoContrato, setArchivoContrato] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/contratos`, axCfg);
      setData(res.data);
    } catch (e) {
      console.log("Endpoint de contratos no encontrado");
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Abrir detalle
  const openDetalle = (item) => {
    setSelectedItem(item);
    setFechaFirma(item.fecha_firma || "");
    setObservaciones(item.observaciones || "");
    setDetalleOpen(true);
  };

  // Generar contrato PDF (descarga desde backend con plantilla corporativa)
  const generarContratoPDF = async (item) => {
    try {
      const response = await axios.get(`${API}/admin/contratos/${item.id}/pdf`, {
        ...axCfg,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Contrato_${item.numero_contrato || item.cliente_nombre}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Contrato PDF descargado");
    } catch (e) {
      toast.error("Error al generar PDF del contrato");
    }
  };

  // Enviar contrato por WhatsApp
  const enviarContratoWhatsApp = (item) => {
    const phone = (item.telefono || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("No hay número de teléfono");
      return;
    }
    
    const msg = `*PETROCONTROL - Contrato de Servicios*\n\nEstimado/a ${item.cliente_nombre},\n\nAdjunto encontrará el contrato de prestación de servicios para ${item.servicio}.\n\nMonto total: $${item.monto_total?.toLocaleString() || 0} MXN + IVA\n\nPor favor revíselo y confirme si está de acuerdo para proceder con la firma.\n\n_Equipo PETROCONTROL_`;
    
    window.open(`https://wa.me/52${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  // Actualizar estado del contrato
  const updateEstado = async (id, nuevoEstado) => {
    try {
      await axios.patch(`${API}/admin/contratos/${id}`, {
        estado: nuevoEstado,
      }, axCfg);
      toast.success(`Contrato marcado como ${STATUS_CONFIG[nuevoEstado].label}`);
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar contrato");
    }
  };

  // Guardar contrato firmado
  const saveContratoFirmado = async () => {
    if (!selectedItem) return;
    if (!fechaFirma) {
      toast.error("Ingrese la fecha de firma");
      return;
    }
    
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/contratos/${selectedItem.id}`, {
        estado: "firmado",
        fecha_firma: fechaFirma,
        observaciones: observaciones,
      }, axCfg);
      
      // Crear Pre-Trámite automáticamente (hereda del contrato)
      await axios.post(`${API}/admin/pretramites`, {
        contrato_id: selectedItem.id,
        quotation_id: selectedItem.cotizacion_id || selectedItem.validacion_id,
        prospect_id: selectedItem.prospecto_id,
        client_id: selectedItem.cliente_id,
        service_type: selectedItem.servicio,
        client_name: selectedItem.cliente_nombre,
        total_amount: selectedItem.monto_total,
        notes: `Contrato firmado el ${fechaFirma}. ${observaciones || ''}`,
        source: "contrato_firmado"
      }, axCfg);
      
      toast.success("Contrato firmado. Pasó a pestaña Pre-Trámite.");
      setDetalleOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Error al guardar contrato");
    } finally {
      setSaving(false);
    }
  };

  // Filtrar datos
  const filteredData = data.filter(item => {
    const matchSearch = !search || 
      item.cliente_nombre?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === "all" || item.estado === filterStatus;
    return matchSearch && matchStatus;
  });

  if (loading) return <p className="text-center py-12 text-gray-500">Cargando contratos...</p>;

  return (
    <div className="space-y-4" data-testid="contrato-tab">
      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gray-500 flex items-center justify-center">
                <Pencil className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#152238]">{data.filter(d => d.estado === "borrador").length}</p>
                <p className="text-xs text-gray-600">Borradores</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <Send className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#152238]">{data.filter(d => d.estado === "enviado").length}</p>
                <p className="text-xs text-gray-600">Enviados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#152238]">{data.filter(d => d.estado === "firmado").length}</p>
                <p className="text-xs text-gray-600">Firmados</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#103F38] flex items-center justify-center">
                <FileSignature className="h-4 w-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-[#152238]">{data.length}</p>
                <p className="text-xs text-gray-600">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl p-3 shadow-sm">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Buscar por cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <span className="text-sm text-gray-500">{filteredData.length} contratos</span>
      </div>

      {/* Lista */}
      {filteredData.length === 0 ? (
        <Card className="border-none shadow-sm">
          <CardContent className="text-center py-16">
            <FileSignature className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay contratos registrados</p>
            <p className="text-sm text-gray-400 mt-1">Los contratos se generan desde Validación cuando los documentos están completos</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filteredData.map((item) => {
            const statusCfg = STATUS_CONFIG[item.estado] || STATUS_CONFIG.borrador;
            const StatusIcon = statusCfg.icon;
            
            return (
              <Card key={item.id} className="border-none shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{item.cliente_nombre}</h3>
                        <Badge className={statusCfg.color}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusCfg.label}
                        </Badge>
                        {item.numero_contrato && (
                          <span className="text-xs font-mono text-gray-500">{item.numero_contrato}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-gray-600 mb-2 flex-wrap">
                        <span className="font-medium text-[#103F38]">{item.servicio}</span>
                        <span className="font-semibold text-lg text-emerald-600">
                          ${item.monto_total?.toLocaleString() || 0}
                        </span>
                      </div>
                      
                      {item.fecha_firma && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          Firmado: {new Date(item.fecha_firma).toLocaleDateString('es-MX')}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 flex-shrink-0">
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => generarContratoPDF(item)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Descargar
                        </Button>
                        
                        {item.estado === "borrador" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-blue-600 border-blue-300"
                            onClick={() => updateEstado(item.id, "enviado")}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Enviar
                          </Button>
                        )}
                        
                        {item.estado === "enviado" && (
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => openDetalle(item)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Registrar Firma
                          </Button>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => enviarContratoWhatsApp(item)}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDetalle(item)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Sheet de Detalle */}
      <Sheet open={detalleOpen} onOpenChange={setDetalleOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <FileSignature className="h-5 w-5 text-[#103F38]" />
              Detalle del Contrato
            </SheetTitle>
          </SheetHeader>
          
          {selectedItem && (
            <div className="py-6 space-y-6">
              {/* Info del contrato */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Cliente:</span>
                  <span className="font-medium">{selectedItem.cliente_nombre}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Servicio:</span>
                  <span className="font-medium text-[#103F38]">{selectedItem.servicio}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Monto Total:</span>
                  <span className="font-bold text-lg text-emerald-600">${selectedItem.monto_total?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Anticipo ({selectedItem.porcentaje_anticipo || 50}%):</span>
                  <span className="font-medium">${((selectedItem.monto_total || 0) * ((selectedItem.porcentaje_anticipo || 50) / 100)).toLocaleString()}</span>
                </div>
              </div>
              
              {/* Registrar firma */}
              {selectedItem.estado !== "firmado" && (
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-gray-700">Registrar Firma</h4>
                  
                  <div>
                    <Label>Fecha de firma</Label>
                    <Input
                      type="date"
                      value={fechaFirma}
                      onChange={(e) => setFechaFirma(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label>Observaciones</Label>
                    <Textarea
                      value={observaciones}
                      onChange={(e) => setObservaciones(e.target.value)}
                      placeholder="Notas sobre la firma..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={saveContratoFirmado}
                    disabled={saving}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    {saving ? "Guardando..." : "Confirmar Contrato Firmado"}
                  </Button>
                </div>
              )}
              
              {/* Info de firma si ya está firmado */}
              {selectedItem.estado === "firmado" && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <CheckCircle2 className="h-5 w-5" />
                    <span className="font-semibold">Contrato Firmado</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Fecha de firma: {selectedItem.fecha_firma ? new Date(selectedItem.fecha_firma).toLocaleDateString('es-MX') : '-'}
                  </p>
                  {selectedItem.observaciones && (
                    <p className="text-sm text-gray-600 mt-2">{selectedItem.observaciones}</p>
                  )}
                </div>
              )}
              
              {/* Acciones */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => generarContratoPDF(selectedItem)}>
                  <Download className="h-4 w-4 mr-2" />
                  Descargar PDF
                </Button>
                <Button 
                  variant="outline" 
                  className="text-green-600 border-green-300"
                  onClick={() => enviarContratoWhatsApp(selectedItem)}
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
